/**
 * Stage sound (2026-09-08). Five cues, bundled as WAV in `public/sfx/` so
 * the venue needs no internet:
 *
 *   round-start      a round begins
 *   player-switch    control of the keyboard passes to the other author
 *   joker-activated  the moderation plays a joker
 *   round-end        a round ends
 *   slide-transition any other new slide/interface — the filler cue, and
 *                    deliberately suppressed while a louder cue is playing
 *                    so the room never hears two effects at once.
 *
 * Browsers refuse audio until the page has seen a gesture. `unlockOnGesture`
 * arms one global listener; until it fires, `audioLocked()` is true and the
 * views offer a "Ton aktivieren" button (the beamer window is opened by
 * script and therefore starts locked).
 */

export type SfxName = 'round-start' | 'player-switch' | 'joker-activated' | 'round-end' | 'slide-transition';

const FILES: Record<SfxName, string> = {
  'round-start': '/sfx/round-start.wav',
  'player-switch': '/sfx/player-switch.wav',
  'joker-activated': '/sfx/joker-activated.wav',
  'round-end': '/sfx/round-end.wav',
  'slide-transition': '/sfx/slide-transition.wav',
};

/** How long a cue keeps the filler cue quiet. */
const FILLER_BLOCK_MS = 1400;

const VOLUME: Record<SfxName, number> = {
  'round-start': 0.85,
  'player-switch': 0.7,
  'joker-activated': 0.8,
  'round-end': 0.85,
  'slide-transition': 0.45,
};

const cache = new Map<SfxName, HTMLAudioElement>();
let unlocked = false;
let lastCueAt = 0;
const lockListeners = new Set<(locked: boolean) => void>();

function element(name: SfxName): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null;
  let el = cache.get(name);
  if (!el) {
    el = new Audio(FILES[name]);
    el.preload = 'auto';
    cache.set(name, el);
  }
  return el;
}

function setUnlocked(value: boolean): void {
  if (unlocked === value) return;
  unlocked = value;
  for (const l of lockListeners) l(!value);
}

export function audioLocked(): boolean {
  return !unlocked;
}

export function onAudioLockChange(cb: (locked: boolean) => void): () => void {
  lockListeners.add(cb);
  return () => lockListeners.delete(cb);
}

/** Warm the cache so the first cue is not late (files are a few hundred kB). */
export function preloadSfx(): void {
  for (const name of Object.keys(FILES) as SfxName[]) element(name)?.load();
}

/** Play a silent-ish probe to satisfy the autoplay policy after a gesture. */
export function unlockAudio(): void {
  const el = element('slide-transition');
  if (!el) return;
  const previous = el.volume;
  el.volume = 0;
  el
    .play()
    .then(() => {
      el.pause();
      el.currentTime = 0;
      el.volume = previous;
      setUnlocked(true);
    })
    .catch(() => {
      el.volume = previous;
    });
}

let gestureArmed = false;
export function unlockOnGesture(): void {
  if (gestureArmed || typeof window === 'undefined') return;
  gestureArmed = true;
  const handler = () => unlockAudio();
  window.addEventListener('pointerdown', handler, { passive: true });
  window.addEventListener('keydown', handler, { passive: true });
}

/**
 * Fire a cue. `filler: true` marks the slide-transition sound, which yields
 * to any louder cue played in the last {@link FILLER_BLOCK_MS} ms.
 */
export function playSfx(name: SfxName, opts: { enabled?: boolean; filler?: boolean } = {}): void {
  if (opts.enabled === false) return;
  const now = Date.now();
  if (opts.filler && now - lastCueAt < FILLER_BLOCK_MS) return;
  const base = element(name);
  if (!base) return;
  // Clone so overlapping cues do not cut each other off mid-play.
  const node = base.cloneNode(true) as HTMLAudioElement;
  node.volume = VOLUME[name];
  node
    .play()
    .then(() => setUnlocked(true))
    .catch(() => setUnlocked(false));
  if (!opts.filler) lastCueAt = now;
}
